/** @jsx jsx */
import { jsx } from "frontend/src/utils/jsx"
import { useRef, useEffect, Fragment } from "react"
import { SectionGrid } from "frontend/src/components/Layout"
import { BlockText } from "../../components/BlockText"
import { Touchable } from "../../components/Touchable"
import moment from "moment-timezone"
import { PDFDownloadLink } from "@react-pdf/renderer"
// import { PDFViewer } from "@react-pdf/renderer"
import MyDocument from "./PDFDocument"
import { useDebugState } from "use-named-state"
/* eslint-disable react-hooks/exhaustive-deps */

const buttonStyles = {
  display: "inline-block",
  px: 5,
  py: 4,
  backgroundColor: "almostBlack",
  color: "background",
  textTransform: "uppercase",
  fontWeight: "light",
  opacity: 1,
  "&:hover": {
    opacity: 0.6,
  },
}

function ErrorWindow({ error }) {
  const { orderNo, zip, extra } = error
  const message = [orderNo, zip, extra].join(" ").trim()
  return (
    <div
      sx={{
        width: "100%",
        borderWidth: "2px",
        borderStyle: "solid",
        borderColor: "accent",
        color: "accent",
        padding: 4,
      }}
    >
      {message}
    </div>
  )
}

function SuccessWindow({ success }) {
  return (
    <div
      sx={{
        width: "100%",
        borderWidth: "2px",
        borderStyle: "solid",
        borderColor: "green_300",
        color: "green_300",
        padding: 4,
        my: 6,
        textAlign: "center",
      }}
    >
      <span role="img" aria-label="checkmark">
        ✔️
      </span>{" "}
      {success}
    </div>
  )
}

function RefundForm({ title, text, mode }) {
  const [orderId, setOrderId] = useDebugState("orderId", null)
  const [orderNumber, setOrderNumber] = useDebugState("orderNumber", "")
  const [zipCode, setZipCode] = useDebugState("zipCode", "")
  const [articles, setArticles] = useDebugState("articles", [])
  const [articleNumber, setArticleNumber] = useDebugState("articleNumber", 0)
  const [discount, setDiscount] = useDebugState("discount", [])
  const [returnArticles, setReturnArticles] = useDebugState("returnArticles", [])
  const [height, setHeight] = useDebugState("height", 0)
  const [error, setError] = useDebugState("error", {
    orderNo: null,
    zip: null,
    extra: null,
  })
  const [showPdf, setShowPdf] = useDebugState("showPdf", false)
  const [success, setSuccess] = useDebugState("success", null)
  const allowedGzG = [
    42044028485856, // Duo 10er Probe
    32213731934304, // Resleep
    40419966877853, // Duo 40er
    45650658296075, // Duo 30er
    45651008225547, // Duo 30er Halbjahresvorrat
    45650775998731, // Duo 30er Jahresvorrat
    37535913738397, // Essehz
    42035992527072, // Gums
    39789307101341, // Spray
    30980542890080, // Drink 7er
    30980542857312, // Drink 14er
    30980542922848, // Drink 28er
    32130834006112, // Gute Nacht Tee
    32130831712352, // Relax Tee
  ]
  const allowedWRR = [
    ...allowedGzG,
    37843232653469, // Akupressurmatte
    39966612062365, // Duftkerze
    37871892037789, // Schlafmaske
    37871610691741, // Teeflasche
    34606369833117, // All you need is sleep.
    34606394278045, // Let's talk about sleep, baby
  ]
  const allowed = mode === "WRR" ? allowedWRR : allowedGzG
  const MyDoc = (
    <MyDocument
      articles={returnArticles}
      number={orderNumber}
      orderId={orderId}
      url={`https://retouren.sleep.ink/?id=${orderId}&name=${orderNumber}&lineItems=${returnArticles
        .map(article => `${article.id}:${article.lineItemId}:${article.returnQuantity}`)
        .join(",")}&mode=${mode}`}
    />
  )
  const WrapperRef = useRef(null)
  try {
    useEffect(() => {
      const postDimensions = () => {
        if (
          window &&
          WrapperRef.hasOwnProperty("current") &&
          height !== WrapperRef.current.offsetHeight
        ) {
          const myHeight = Math.max(
            height,
            WrapperRef.current.offsetHeight,
            WrapperRef.current.scrollHeight
          )
          setHeight(myHeight)
          window.parent.postMessage({ frameHeight: myHeight }, "*")
          /*console.log(myHeight);*/
        }
      }
      postDimensions()
      if (window) {
        window.onload = () => postDimensions()
        window.onresize = () => postDimensions()
      }
    }, [error, showPdf, articles])
  } catch (error) {
    console.dir(error)
  }
  const handleOrdnerNoChange = async (event, modus) => {
    event.preventDefault()
    if (modus !== "change" && modus !== "paste") return null
    let ono
    if (modus === "change") {
      ono = event.target.value
    } else {
      ono = event.clipboardData.getData("text").substr(0, 8)
    }
    setShowPdf(false)
    if (/^([Aa]{1}[0-9]{7})$/.test(ono)) {
      setOrderNumber(ono)
      setError({ ...error, orderNo: null, extra: null })
    } else if (ono === "") {
      setOrderNumber(ono)
      setError({ ...error, orderNo: "Sie müssen eine Bestellnummer eingeben.", extra: null })
    } else {
      setError({ ...error, orderNo: "Die Bestellnummer ist nicht korrekt.", extra: null })
      setOrderNumber(ono)
    }
  }
  const handleZipCodeChange = (event, modus) => {
    event.preventDefault()
    if (modus !== "paste" && modus !== "change") return null
    let plz
    if (modus === "change") {
      plz = event.target.value
    } else {
      plz = event.clipboardData.getData("text").substr(0, 5)
    }
    setShowPdf(false)
    if (/^([0-9]{4,5})$/.test(plz)) {
      setZipCode(plz)
      setError({ ...error, zip: null, extra: null })
    } else if (plz === "") {
      setZipCode(plz)
      setError({ ...error, zip: "Sie müssen eine Postleitzahl eingeben.", extra: null })
    } else {
      setZipCode(plz)
      setError({ ...error, zip: "Die eingegebene Postleitzahl ist ungültig.", extra: null })
    }
  }
  const togglePdf = e => {
    e.preventDefault()
    if (returnArticles.length > 0) {
      setShowPdf(true)
    } else {
      setShowPdf(false)
    }
  }
  const getOrderData = async e => {
    e.preventDefault()
    if (error.orderNo == null && error.zip == null) {
      const response = await fetch(
        `/.netlify/functions/orderData?code=${orderNumber}&zip=${zipCode}&mode=${mode}`
      )
      if (response.status === 500) {
        setError({ ...error, extra: "Die Bestellung wurde nicht gefunden." })
      } else if (response.status === 401) {
        setError({
          ...error,
          extra:
            "Zu dieser Kombination von Bestellnummer und Postleitzahl liegt uns keine Bestellung vor. Bitte überprüfen Sie Ihre Eingabe. Sollten Sie weiterhin Probleme in der Erstellung des Rücksendedokuments haben, kontaktieren Sie uns gerne direkt.",
        })
      } else if (response.status === 420) {
        setError({
          ...error,
          extra:
            "Diese Bestellung ist von der Geld-zurück-Garantie ausgeschlossen, weil ihr Warenwert inklusive Rabatte unter dem Mindestbestellwert liegt.",
        })
      } else {
        const data = await response.json()
        const {
          id,
          created_at: createdAt,
          line_items: lineItems = [],
          discount_applications = [],
        } = await data
        const now = moment().tz("Europe/Berlin")
        const create = moment(createdAt).tz("Europe/Berlin")
        const diff = now.diff(create, "days")
        const maxDiff = mode === "WRR" ? 24 : 40
        const singleLineItem =
          lineItems.length === 1 &&
          lineItems[0].quantity === 1 &&
          allowed.includes(lineItems[0].variant_id)
        const transformedLineItems = singleLineItem
          ? await lineItems.map(item => {
              const { name, price, quantity, sku, variant_id, id: lineItemId } = item
              return {
                name,
                price,
                quantity,
                sku,
                returnQuantity: 1,
                checked: true,
                allowed: allowed.includes(variant_id),
                lineItemId,
                id: variant_id,
              }
            })
          : await lineItems.map(item => {
              const { name, price, quantity, sku, variant_id, id: lineItemId } = item
              return {
                name,
                price,
                quantity,
                sku,
                returnQuantity: 1,
                checked: false,
                allowed: allowed.includes(variant_id),
                lineItemId,
                id: variant_id,
              }
            })
        if (diff <= maxDiff) {
          const numberOfArticles = await transformedLineItems.reduce(
            (acc, curr) => acc + curr.quantity,
            0
          )
          setArticleNumber(await numberOfArticles)
          setOrderId(await id)
          setArticles(await transformedLineItems)
          setError({ orderNo: null, zip: null, extra: null })
          setDiscount(discount_applications)
          setSuccess("Ihr Rücksendeschein wurde generiert.")
          if (singleLineItem) {
            setReturnArticles(await transformedLineItems)
            setShowPdf(true)
          }
        } else {
          setError({
            orderNo: null,
            zip: null,
            extra:
              mode === "WRR"
                ? "Leider kann die Bestellung nicht bearbeitet werden, weil die Frist zur Einlösung des Widerrufsrecht überschritten ist."
                : "Leider kann die Bestellung nicht bearbeitet werden, weil die Frist zur Einlösung der Geld-zurück-Garantie überschritten ist.",
          })
          setSuccess(null)
          setArticles([])
          setDiscount([])
          setReturnArticles([])
          setOrderId("")
          setShowPdf(false)
        }
      }
    }
  }
  const handleChangeCheckbox = event => {
    setShowPdf(false)
    const { index } = event.target.dataset
    const newLineItems = articles.slice()
    newLineItems[index].checked = !articles[index].checked
    const checkedArticles = newLineItems.filter(item => item.checked)
    setArticles(newLineItems)
    setReturnArticles(checkedArticles)
  }
  const handleChangeQuantity = event => {
    setShowPdf(false)
    const { index } = event.target.dataset
    const newLineItems = articles.slice()
    newLineItems[index].returnQuantity = Number(event.target.value)
    const checkedArticles = newLineItems.filter(item => item.checked)
    setReturnArticles(checkedArticles)
  }
  return (
    <SectionGrid sx={{ py: [8, 8, 11] }} Ref={WrapperRef}>
      <SectionGrid.narrow sx={{ px: [4, 4, 0] }}>
        <h2 sx={{ variant: "text.h2", mb: 7, textAlign: "center" }}>{title}</h2>
        {text && <BlockText blocks={text} />}
        {(error.orderNo != null || error.zip != null || error.extra != null) && (
          <ErrorWindow error={error} />
        )}
        <div
          sx={{
            mt: 7,
            mb: 5,
            display: "flex",
            flexDirection: ["column", "column", "row"],
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <label for="orderNumber">Bestellnummer / Referenz (z.B. A5451999)</label>
          <input
            type="text"
            placeholder="Bestellnummer"
            name="orderNumber"
            // @ts-ignore
            maxLength="9"
            value={orderNumber}
            onChange={e => handleOrdnerNoChange(e, "change")}
            onPaste={e => handleOrdnerNoChange(e, "paste")}
            sx={{
              padding: 4,
              fontSize: 1,
              color: "almostBlack",
              borderColor: "rgba(47,78,37,0,15)",
              borderStyle: "solid",
              borderWidth: "1px",
              fontWeight: "light",
              mr: [0, 0, 8],
              mb: [3, 3, 0],
            }}
          />
        </div>
        <div
          sx={{
            mt: 7,
            mb: 5,
            display: "flex",
            flexDirection: ["column", "column", "row"],
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <label for="zipCode">Postleitzahl (D/A/CH)</label>
          <input
            type="text"
            placeholder="Postleitzahl"
            name="zipCode"
            value={zipCode}
            onChange={e => handleZipCodeChange(e, "change")}
            onPaste={e => handleZipCodeChange(e, "paste")}
            // @ts-ignore
            maxLength="5"
            sx={{
              padding: 4,
              fontSize: 1,
              color: "almostBlack",
              borderColor: "rgba(47,78,37,0,15)",
              borderStyle: "solid",
              borderWidth: "1px",
              fontWeight: "light",
              mr: [0, 0, 8],
              mb: [3, 3, 0],
            }}
            /* if enter is pressed, get order data */
            onKeyPress={e => {
              e.key === "Enter" && getOrderData(e)
            }}
          />
        </div>
        <div
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Touchable
            sx={{
              ...buttonStyles,
              cursor: error.orderNo == null && error.zip == null ? "pointer" : "not-allowed",
            }}
            onClick={getOrderData}
            disabled={
              error.orderNo == null && error.zip == null
                ? orderNumber.length === 0 || zipCode.length === 0
                  ? true
                  : false
                : true
            }
          >
            Artikel Anzeigen
          </Touchable>
        </div>
        {orderId && (
          <div>
            {showPdf && articleNumber === 1 ? (
              <Fragment>
                <h3 sx={{ variant: "text.h4", mt: 5, mb: 3 }}>
                  Artikel zur Rücksendung auswählen:
                </h3>
                <div
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "flex-start",
                    alignItems: "center",
                    mb: 3,
                  }}
                >
                  <input
                    type="checkbox"
                    name={`${articles[0].sku}-0`}
                    data-index={0}
                    onChange={() => {
                      return false
                    }}
                    checked={articles[0].checked}
                    readOnly={true}
                    sx={{ mr: [3, 4, 7] }}
                  />
                  <label
                    htmlFor={`${articles[0].sku}-0`}
                    sx={{ textAlign: "left", flexBasis: "50%" }}
                  >
                    {articles[0].name}
                  </label>
                  <input
                    type="number"
                    min="1"
                    max={`${articles[0].quantity}`}
                    defaultValue="1"
                    data-index={0}
                    readOnly={true}
                    onChange={() => {
                      return false
                    }}
                    sx={{
                      maxWidth: "60px",
                      padding: 4,
                      color: "almostBlack",
                      borderColor: "rgba(47,78,37,0,15)",
                      borderStyle: "solid",
                      borderWidth: "1px",
                      fontWeight: "light",
                      ml: [2, 3, 6],
                      mr: [4, 5, 9],
                    }}
                  />
                  {discount.length === 1 && discount[0].value_type === "percentage" ? (
                    <div sx={{ flexBasis: "20%", textAlign: "right" }}>{`${(
                      articles[0].quantity *
                      articles[0].price *
                      (1 - 0.01 * Number(discount[0].value))
                    )
                      .toFixed(2)
                      .replace(".", ",")} €`}</div>
                  ) : discount.length === 0 ? (
                    <div>{`${(articles[0].quantity * articles[0].price)
                      .toFixed(2)
                      .replace(".", ",")} €`}</div>
                  ) : (
                    <div />
                  )}
                </div>
                <SuccessWindow success={success} />
                <div sx={{ textAlign: "center" }}>
                  <PDFDownloadLink
                    document={MyDoc}
                    fileName={`Rücksendedokument_${orderNumber}.pdf`}
                    sx={{ ...buttonStyles, cursor: "pointer", textDecoration: "none" }}
                  >
                    PDF herunterladen
                  </PDFDownloadLink>
                </div>
              </Fragment>
            ) : articles.length >= 1 ? (
              <div id="articles" sx={{ mb: 8, mt: 8 }}>
                <h3 sx={{ variant: "text.h4", mt: 5, mb: 3 }}>
                  Artikel zur Rücksendung auswählen:
                </h3>
                {articles.map((article, index) => {
                  const { sku, name, quantity, price, allowed: itemAllowed } = article
                  console.log(sku, itemAllowed)
                  return (
                    <div
                      key={index}
                      sx={{
                        width: "100%",
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "flex-start",
                        alignItems: "center",
                        mb: 3,
                      }}
                    >
                      <input
                        type="checkbox"
                        name={`${sku}-${index}`}
                        data-index={index}
                        onChange={
                          itemAllowed
                            ? handleChangeCheckbox
                            : () => {
                                return false
                              }
                        }
                        checked={article.checked}
                        readOnly={itemAllowed ? false : true}
                        sx={{ mr: [3, 4, 7] }}
                      />
                      <label
                        htmlFor={`${sku}-${index}`}
                        sx={{ textAlign: "left", flexBasis: "50%" }}
                      >
                        {name}
                      </label>
                      <input
                        type="number"
                        min="1"
                        max={`${quantity}`}
                        defaultValue="1"
                        data-index={index}
                        readOnly={itemAllowed ? false : true}
                        onChange={handleChangeQuantity}
                        sx={{
                          maxWidth: "60px",
                          padding: 4,
                          color: "almostBlack",
                          borderColor: "rgba(47,78,37,0,15)",
                          borderStyle: "solid",
                          borderWidth: "1px",
                          fontWeight: "light",
                          ml: [2, 3, 6],
                          mr: [4, 5, 9],
                        }}
                      />
                      {discount.length === 1 && discount[0].value_type === "percentage" ? (
                        <div sx={{ flexBasis: "20%", textAlign: "right" }}>{`${(
                          quantity *
                          price *
                          (1 - 0.01 * Number(discount[0].value))
                        )
                          .toFixed(2)
                          .replace(".", ",")} €`}</div>
                      ) : discount.length === 0 ? (
                        <div>{`${(quantity * price).toFixed(2).replace(".", ",")} €`}</div>
                      ) : (
                        <div />
                      )}
                    </div>
                  )
                })}
                <div sx={{ textAlign: "center" }}>
                  <Touchable
                    sx={{
                      px: 5,
                      py: 4,
                      backgroundColor: "almostBlack",
                      color: "background",
                      textTransform: "uppercase",
                      fontWeight: "light",
                      cursor: returnArticles.length > 0 ? "pointer" : "not-allowed",
                    }}
                    onClick={togglePdf}
                    disabled={returnArticles.length > 0 ? false : true}
                  >
                    PDF generieren
                  </Touchable>
                </div>
              </div>
            ) : (
              <div />
            )}
            {showPdf && articleNumber > 1 && returnArticles.length >= 1 && (
              <Fragment>
                <SuccessWindow success={success} />
                <div sx={{ textAlign: "center" }}>
                  <PDFDownloadLink
                    document={MyDoc}
                    fileName={`Rücksendedokument_${orderNumber}.pdf`}
                    sx={{ ...buttonStyles, cursor: "pointer", textDecoration: "none" }}
                  >
                    PDF herunterladen
                  </PDFDownloadLink>
                </div>
              </Fragment>
            )}
          </div>
        )}
      </SectionGrid.narrow>
    </SectionGrid>
  )
}

export { RefundForm }
